/**
 * CueRail — the Brass Dial Strip (DESIGN_SPEC §4.2, v1 horizontal-rail shape).
 *
 * Horizontal timeline. Each cue is a tick in brass-tarnish; the active
 * playhead is a brass-strike vertical line at `currentSeconds`. Tick
 * positions are computed against the rail's max-time.
 */

import { cueRailCopy } from "./CueRail.copy";

export interface Cue {
  readonly id: string;
  readonly kind: string;
  readonly label: string;
  readonly at_seconds: number;
}

export interface CueRailProps {
  readonly cues: readonly Cue[];
  readonly currentSeconds?: number;
  readonly onSelect?: (id: string) => void;
}

function railMaxSeconds(cues: readonly Cue[], currentSeconds: number | undefined): number {
  const fromCues = cues.reduce((max, c) => Math.max(max, c.at_seconds), 0);
  return Math.max(fromCues, currentSeconds ?? 0, 1);
}

const tickStyle = (pct: number, interactive: boolean): React.CSSProperties => ({
  position: "absolute",
  left: `calc(${pct}% + 0.75rem - ${pct * 0.015}rem)`,
  top: "0.5rem",
  bottom: "0.5rem",
  width: "2px",
  padding: 0,
  border: 0,
  background: "var(--brass-tarnish)",
  cursor: interactive ? "pointer" : "default",
});

export function CueRail({
  cues,
  currentSeconds,
  onSelect,
}: CueRailProps): React.ReactElement {
  const max = railMaxSeconds(cues, currentSeconds);
  const interactive = typeof onSelect === "function";

  if (cues.length === 0) {
    return (
      <div
        data-testid="cue-rail"
        role="group"
        aria-label={cueRailCopy.rulerLabel}
        className="bg-stage-pit border-ledger-line rounded-ledger-line font-plex-mono text-paper-cream-dim"
        style={{ padding: "0.75rem 1rem", fontSize: "0.781rem" }}
      >
        {cueRailCopy.emptyLabel}
      </div>
    );
  }

  return (
    <div
      data-testid="cue-rail"
      role="group"
      aria-label={cueRailCopy.rulerLabel}
      className="bg-stage-pit border-ledger-line rounded-ledger-line"
      style={{ position: "relative", height: "48px", padding: "0.5rem 0.75rem" }}
    >
      <div style={{ position: "absolute", left: "0.75rem", right: "0.75rem", top: "50%", height: "1px", background: "var(--measure-line)" }} />
      {cues.map((cue) => (
        <button
          key={cue.id}
          type="button"
          data-testid={`cue-tick-${cue.id}`}
          data-kind={cue.kind}
          aria-label={`${cueRailCopy.cueAriaPrefix} ${cue.label}`}
          title={cue.label}
          disabled={!interactive}
          onClick={interactive ? (): void => onSelect?.(cue.id) : undefined}
          style={tickStyle((cue.at_seconds / max) * 100, interactive)}
        />
      ))}
      {typeof currentSeconds === "number" ? (
        <div
          data-testid="cue-playhead"
          aria-label={cueRailCopy.playheadLabel}
          style={{
            position: "absolute",
            left: `calc(${(currentSeconds / max) * 100}% + 0.75rem)`,
            top: "0.25rem",
            bottom: "0.25rem",
            width: "1px",
            background: "var(--brass-strike)",
            pointerEvents: "none",
          }}
        />
      ) : null}
    </div>
  );
}

export default CueRail;
